// Epic 7 re-skin (ADO-903) — the unified /products catalogue page.
//
// Consolidates the former /browse and /search screens under one URL and one
// two-bar SiteHeader (the customer rejected the separate /browse and /search
// URLs) and lays them out like the designer prototype's category-listing:
//   • Browse (Story 7.1 department selector) is a HEADER MEGA-MENU — the Browse
//     button opens BrowseMenu under the sub-nav (the column mega-menu, or the
//     side tray on mobile, ADO-1779).
//   • the body is the CategoryListing (filters sidebar + product grid) for the
//     selected node, auto-loaded to the first department so the grid is populated
//     on arrival; a committed search swaps it for the ranked search results.
// Browse/search/CategoryListing keep all their tested data logic (drill,
// load-more guard, search abort/seq guard, filters, availability, favourites).
//
// The auth gate lives on the /products route in main.tsx.

import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UNSAFE_NavigationContext, useLocation, useNavigate, useSearchParams } from 'react-router';
import { SiteHeader, useHeaderLayout } from '../../design-system';
import { PLANOGRAM_NAV, usePlanogramAvailability } from '../../planogram/usePlanogramAvailability';
import { PlanogramChip } from '../Planograms/PlanogramChip';
import { useSessionStore } from '../../auth/useSessionStore';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { isAdminRole } from '../../auth/rbac';
import {
  appendScopeParams,
  productScopeFromSearch,
  PRODUCT_SCOPE_PARAM_KEYS,
  type CategoryNode,
  type ProductScope,
} from '../../api/browse.client';
import {
  appendFilterParams,
  FACET_KEYS,
  type FacetKey,
  type ProductFilters,
} from '../../api/facets';
import { SCOPE_LABEL_PARAM } from '../../api/landing.client';
import {
  appendCollectedOffersParam,
  COLLECTED_OFFERS_PARAM,
  collectedOffersFromSearch,
} from './offersCollection';
import { BrowseMenu } from '../Browse/BrowseMenu';
import { CategoryListing, type ListingRequest } from '../Browse/CategoryListing';
import { useBrowseDrill } from '../Browse/useBrowseDrill';
import { ProductSearch } from '../Search/ProductSearch';
import { useHeaderChrome } from './useHeaderChrome';
import { SearchTypeahead } from './SearchTypeahead';
import { recordRecentSearch } from './recentSearches';
import {
  COLLECTION_FILTER_KEYS,
  dropListingFacets,
  isProductsSearchCanonical,
  normaliseSearchTerm,
  parseProductFilters,
  parseProductsUrlState,
  PRODUCTS_PARAMS,
  productsPath,
  writeBrowsePagination,
  writeProductsUrlState,
} from './productsUrlState';
import { ScanModal } from '../Scan/ScanModal';
import { detectScanCapability } from '../Scan/scanCapability';
import { BROWSE_MENU_ID } from '../Browse/useBrowseMegaMenu';
import styles from './Products.module.css';

export interface ProductsProps {
  /** Epic 7 demo polish (2026-07-17) — `favourites` renders the SAME page shell
   *  with the listing in favourites view (prototype `?view=favourites`); any
   *  taxonomy navigation from there routes back to /products. */
  readonly view?: 'catalogue' | 'favourites';
}

export function Products({ view = 'catalogue' }: ProductsProps = {}) {
  const favouritesView = view === 'favourites';
  const navigate = useNavigate();

  // Story 7.35 (ADO-2032) — the address bar is the single source of truth for the
  // catalogue view: the committed search term (`q`), the selected node, the active
  // facets and the browse page/size all live in the query string, so a shareable
  // link, a reload, Back/Forward and a bookmark all restore the same view
  // (Decision 1). Everything is derived from `searchKey`, memoised so a render that
  // doesn't touch the URL re-uses the same parsed state (and the listing's fetch
  // effect only re-runs when the URL really changes).
  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const urlState = useMemo(
    () => parseProductsUrlState(new URLSearchParams(searchKey)),
    [searchKey],
  );
  // Story 8.1 (ADO-2010), Tasks 8.2-8.4 — the landing-tile scope also rides in the
  // URL (`?hierarchyCode=…&cateringOnly=true`). `scopeLabel` is web-only (the
  // chip's name) and is never sent to the API.
  // The facets (below, as `requestFilters`) and the scope are memoised on their serialised
  // form, not on the whole query string, so a change to anything else (a canonicalising
  // rewrite, an unowned param, the page) keeps their identity and does not re-run the listing
  // and search fetches that depend on them.
  const scopeQuery = scopeQueryOf(productScopeFromSearch(new URLSearchParams(searchKey)));
  const scope = useMemo(
    () => productScopeFromSearch(new URLSearchParams(scopeQuery)),
    [scopeQuery],
  );
  const scopeLabel = new URLSearchParams(searchKey).get(SCOPE_LABEL_PARAM) ?? undefined;
  // Story 7.31 (ADO-1113), AC1: the collected-offers union rides in the URL too
  // (`/products?offers=all`), so the one-place reach survives a reload, a Back, a paste, a node
  // switch and a scope change exactly as the offer facet does.
  const collectedOffers = useMemo(
    () => collectedOffersFromSearch(new URLSearchParams(searchKey)),
    [searchKey],
  );

  // The ONE URL writer. Apply an owned-state change, preserving every non-owned
  // parameter (the Story 8.1 scope, the Story 7.31 `offers` marker, any future
  // campaign param). A user action pushes a new history entry (so Back returns to
  // the prior view); a sanitisation / server canonicalisation replaces it
  // (Decisions 10-11). A change that leaves the query string as it is writes
  // nothing (the router would push a duplicate entry that Back then has to step
  // through); the return value says whether it wrote.
  //
  // It builds on the LATEST URL, which is the router's history location, not the render-time
  // `searchKey`. The router's `push` / `replace` (and Back/Forward, a <Link>) move the history
  // synchronously, while the render that shows the move runs later, in a transition; under
  // BrowserRouter the history reads `window.location`. So `searchKey` lags in two ways: one
  // handler can write twice (the brand single-select removes the old value, then adds the new
  // one), and an urgent render (a store update) can run while a pushed write is still in the
  // transition. Either way the next write still sees the newest URL.
  //
  // A write made on top of this writer's own write that has not yet committed folds into that
  // write's history entry (a replace), so one gesture is one Back step. A write on top of an
  // entry the shopper moved to (Back/Forward) pushes as usual.
  const { navigator: routerHistory } = useContext(UNSAFE_NavigationContext);
  const { pathname } = useLocation();
  // The URL last committed, and this writer's last write while it has not yet committed.
  const committedSearchRef = useRef(searchKey);
  const pendingWriteRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    committedSearchRef.current = searchKey;
    if (pendingWriteRef.current === searchKey) pendingWriteRef.current = null;
  }, [searchKey]);
  const latestUrl = useCallback((): { pathname: string; search: string } => {
    const location = (routerHistory as Partial<{ location: { pathname: string; search: string } }>)
      .location;
    // A router whose history does not expose its location (none of the declarative routers):
    // this writer's own pending write, else the committed URL, is all there is to go on.
    if (location === undefined) {
      return { pathname, search: pendingWriteRef.current ?? committedSearchRef.current };
    }
    // The app's router has no basename, so the history's pathname compares with the route's.
    return {
      pathname: location.pathname,
      search: new URLSearchParams(location.search).toString(),
    };
  }, [routerHistory, pathname]);
  const writeUrl = useCallback(
    (next: URLSearchParams, replace: boolean): boolean => {
      const latest = latestUrl();
      // The history has already left this screen (a <Link> elsewhere): nothing to write onto.
      if (latest.pathname !== pathname) return false;
      const nextKey = next.toString();
      if (nextKey === latest.search) return false;
      const folded =
        latest.search === pendingWriteRef.current && latest.search !== committedSearchRef.current;
      pendingWriteRef.current = nextKey;
      setSearchParams(next, { replace: replace || folded });
      return true;
    },
    [latestUrl, pathname, setSearchParams],
  );
  const applyUrl = useCallback(
    (mutate: (params: URLSearchParams) => void, replace = false): boolean => {
      const next = new URLSearchParams(latestUrl().search);
      mutate(next);
      return writeUrl(next, replace);
    },
    [latestUrl, writeUrl],
  );
  const changeScope = useCallback(
    (next: ProductScope) => {
      // Story 7.35 (ADO-2032): replace only the scope block, keeping the node /
      // search / facets the URL already carries. A changed result scope starts
      // from its first page at the applied size (a user preference, Story 7-FU-C).
      // This is a user action, so it creates a normal history entry.
      applyUrl((params) => {
        const label = params.get(SCOPE_LABEL_PARAM);
        for (const key of PRODUCT_SCOPE_PARAM_KEYS) params.delete(key);
        params.delete(SCOPE_LABEL_PARAM);
        params.delete(PRODUCTS_PARAMS.page);
        appendScopeParams(params, next);
        if (
          label !== null &&
          (next.hierarchyCode !== undefined || next.globalFavourite !== undefined)
        ) {
          params.set(SCOPE_LABEL_PARAM, label);
        }
      });
    },
    [applyUrl],
  );
  // A10.4: the Catering-only switch is offered to Foodservice accounts only.
  const offerCateringOnly = useSessionStore(
    (s) => s.activeTradingAccount?.tradingAccountType === 'FOODSERVICE',
  );

  // Story 7.35 (ADO-2032) — the live search box value, seeded from the URL's
  // committed term and kept in step with it on a navigation (deep link, Back),
  // while leaving in-progress typing alone until the next commit. `searchNonce`
  // forces an identical re-submit to re-run the search even though the URL (hence
  // the committed term) is unchanged — page-mode parity.
  const [searchInput, setSearchInput] = useState(urlState.q);
  const [searchNonce, setSearchNonce] = useState(0);
  const committedTerm = urlState.q;
  const searching = urlState.searching;
  useEffect(() => {
    setSearchInput(urlState.q);
  }, [urlState.q]);

  // Favourites badge + depot switcher — the header chrome shared verbatim with
  // My Account / Admin Users (PR #148 review). The catalogue owns its own search
  // (inline, with typeahead) and Browse (the mega-menu below), so it composes the
  // base chrome hook rather than the catalogue-linking useCatalogueHeader.
  const {
    favouritesCount,
    depots,
    activeDepotId,
    onSelectDepot,
    depotName,
    depotCutOff,
    logo,
    themed,
    ...cart
  } = useHeaderChrome();

  // The active-basket lifecycle (open the depot's DRAFT basket, hydrate it, and
  // re-open on a depot switch) is mounted by useHeaderChrome above, like on every
  // other full-header screen.

  // Search typeahead (Epic 7 pixel parity, ADO-903): the dropdown opens on
  // focus/keystroke and closes on commit/clear (here) or Escape/click-outside
  // (inside SearchTypeahead). `typeaheadGroupId` is the dropdown's DOM id (the
  // suggestions are a labelled group of buttons, not an ARIA combobox listbox —
  // PR #139 review).
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const typeaheadGroupId = useId();

  // Browse mega-menu (Story 7.1 department selector) + the drill-state it drives.
  // No default Department (2026-07-17 demo feedback): the root state lists ALL
  // departments' products; drilling is an explicit filter the user applies.
  const drill = useBrowseDrill();
  const [browseOpen, setBrowseOpen] = useState(false);

  // Enabler E7-C (ADO-2274), D-1: the listing's page and facets belong to one ordering entry
  // and start afresh when it changes (a depot switch changes it too). They live in the URL now
  // (Story 7.35, ADO-2032), so a switch drops the listing's facets and `page` from it with a
  // REPLACE, keeping `node`, `q`, `pageSize` and a collection (the offer facet, the `offers`
  // marker, the scope). `entryOfUrl` is the entry the URL's facets were chosen under; until
  // the write lands, the switched-to entry is asked without them on page 1, so it never sees
  // the previous entry's selection. An entry arriving for the first time (the session
  // resolving under a deep link) is not a switch and keeps the link's facets.
  const catalogueEntryKey = useCatalogueEntryKey();
  const [entryOfUrl, setEntryOfUrl] = useState(catalogueEntryKey);
  const entrySwitchPending = entryOfUrl !== null && entryOfUrl !== catalogueEntryKey;
  const requestFiltersQuery = filtersQueryOf(
    entrySwitchPending ? collectionFiltersOf(urlState.filters) : urlState.filters,
  );
  const requestFilters = useMemo(
    () => parseProductFilters(new URLSearchParams(requestFiltersQuery)),
    [requestFiltersQuery],
  );
  const requestPage = entrySwitchPending ? 1 : urlState.page;

  // Camera barcode scan (Story 7.18, ADO-1387). The trigger is capability-gated
  // (AC1) — detected once per mount; the scan flow lives entirely in ScanModal, a
  // separate surface from the search box, so a scanned barcode NEVER lands in
  // `searchInput` (AC8 holds by construction — there is no code path here that writes
  // the barcode into the search state).
  const [scanOpen, setScanOpen] = useState(false);
  const [scanCapability] = useState(detectScanCapability);
  // The scan modal's "Search by name" fallback (AC7/AC12) returns the user to text
  // search — focus the header input once the modal has closed and restored focus.
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Story 7.35 (ADO-2032), Decision 11 — a malformed or stale URL is sanitised to
  // its canonical form before anything is requested: a repeated owned key, an
  // invalid node UUID, a `page`/`pageSize` outside bounds, a defaulted value left
  // in the URL, or pagination left under an active search. A canonicalising
  // rewrite REPLACES history (no Back-trap on a link the user never typed). The
  // fetches below read the PARSED (already sanitised) state, so a request never
  // carries a malformed value even on the render before the URL settles. Favourites
  // view owns no URL state, so it is left untouched.
  // It sanitises the latest URL, which is the rendered one unless a newer navigation is pending.
  useEffect(() => {
    if (favouritesView) return;
    const current = new URLSearchParams(latestUrl().search);
    const currentState = parseProductsUrlState(current);
    if (!isProductsSearchCanonical(current, currentState)) {
      writeUrl(writeProductsUrlState(current, currentState), true);
    }
    // `searchKey` re-runs this on every committed URL.
  }, [favouritesView, latestUrl, searchKey, writeUrl]);

  // The D-1 entry switch (see `entryOfUrl` above): drop the previous entry's facets and page,
  // then adopt the new entry once the cleaned URL is in (the router applies the replace on a
  // later render, and adopting earlier would ask the new entry with the old selection).
  useEffect(() => {
    if (entryOfUrl === catalogueEntryKey) return;
    if (entrySwitchPending && !favouritesView) {
      const wrote = applyUrl((params) => {
        dropListingFacets(params);
        params.delete(PRODUCTS_PARAMS.page);
      }, true);
      if (wrote) return;
    }
    setEntryOfUrl(catalogueEntryKey);
    // `searchKey` re-runs this once the cleaned URL is in, to adopt the new entry.
  }, [applyUrl, catalogueEntryKey, entryOfUrl, entrySwitchPending, favouritesView, searchKey]);

  // Story 7.35 (ADO-2032) — commit a browse node selection to the URL (the single
  // source of truth): set `node` (or clear it for the ROOT), drop any active
  // search + facets + page (a node change starts a fresh listing), and drive
  // the drill optimistically so the mega-menu columns update before the listing
  // resolves. The applied page size carries across (a user preference, Story
  // 7-FU-C; a search never carries one, Decision 7). A user action — normal history.
  // Re-selecting the node the listing already shows, with no search active, is not a
  // node change: only the drill moves, and the listing's facets and page stay put.
  const selectBrowseNode = useCallback(
    (node: CategoryNode | null) => {
      drill.navigate(node);
      applyUrl((params) => {
        const current = parseProductsUrlState(params);
        if (!current.searching && current.node === (node?.id ?? null)) return;
        params.delete(PRODUCTS_PARAMS.q);
        dropListingFacets(params);
        params.delete(PRODUCTS_PARAMS.page);
        if (node === null) params.delete(PRODUCTS_PARAMS.node);
        else params.set(PRODUCTS_PARAMS.node, node.id);
      });
    },
    [applyUrl, drill],
  );

  // The mega-menu and "View all" leave the favourites view for the catalogue page via a
  // shareable URL (Story 7.35, ADO-2032: `?node=` instead of a transient router state handover;
  // `null` = the all-products root). Story 7.29 (ADO-2025, D-7.29-35): the rail's Department and
  // Category narrow the favourites instead, as SL1.0's do, through the same drill as the
  // catalogue. The favourites view owns no URL state, so that drill stays in memory there.
  const goToCatalogue = useCallback(
    (node: CategoryNode | null) => {
      navigate(productsPath({ node: node?.id ?? null }));
    },
    [navigate],
  );
  const listingNavigate = favouritesView ? drill.navigate : selectBrowseNode;

  // Manager-dashboard header link (Story 6.31, ADO-1253) — shown only to users who
  // can reach /admin/users (DISPLAY/ROUTING only; server re-checks, NFR18).
  const role = useSessionStore((s) => s.role);
  // Story 7.33 (ADO-2026): the Planogram entry, where the server offers the shop plans: in the
  // header beside Offers, or, on a phone, as a chip beside the listing's Filters.
  const planogramsAvailable = usePlanogramAvailability();
  const phoneLayout = useHeaderLayout() === 'mobile';

  // Commit `term` as the active search by writing `q` to the URL: a NEW term drops
  // the facets (they are scoped to a search, mirroring the per-term reset) and any
  // browse pagination; the prior `node` is RETAINED so clearing the search returns
  // to that category (Decision 7). An identical re-submit leaves the URL as it is,
  // so it bumps `searchNonce` to re-run the search instead; a changed URL re-runs
  // it by itself (bumping the nonce as well would run it once more). Every commit
  // path funnels here (Enter, a suggestion row's product name, a recent-term row,
  // "View all").
  const commitSearch = useCallback(
    (term: string) => {
      const committed = normaliseSearchTerm(term);
      if (committed !== '') recordRecentSearch(committed);
      setBrowseOpen(false);
      setTypeaheadOpen(false);
      const wrote = applyUrl((params) => {
        const termChanged = committed !== normaliseSearchTerm(params.get(PRODUCTS_PARAMS.q) ?? '');
        // An empty commit with no search active neither enters nor leaves one: the
        // listing's facets and pagination stay, and nothing is written.
        if (committed === '' && !termChanged) return;
        params.delete(PRODUCTS_PARAMS.page);
        params.delete(PRODUCTS_PARAMS.pageSize);
        if (termChanged) dropListingFacets(params);
        if (committed === '') params.delete(PRODUCTS_PARAMS.q);
        else params.set(PRODUCTS_PARAMS.q, committed);
      });
      if (!wrote) setSearchNonce((n) => n + 1);
    },
    [applyUrl],
  );
  const submitSearch = () => commitSearch(searchInput);
  // A typeahead commit also places the committed term into the search box.
  const commitFromTypeahead = (term: string) => {
    setSearchInput(term);
    commitSearch(term);
  };
  // Clear the search: drop `q` + the search facets, KEEPING the `node` so the
  // listing returns to the category the search was launched from (Decision 7). A
  // user action — normal history. With no active search there is nothing to exit:
  // only the box is cleared, and the listing's facets and pagination stay put.
  const clearSearch = useCallback(() => {
    setSearchInput('');
    setTypeaheadOpen(false);
    if (!searching) return;
    applyUrl((params) => {
      params.delete(PRODUCTS_PARAMS.q);
      dropListingFacets(params);
    });
  }, [applyUrl, searching]);

  // Story 7.35 (ADO-2032) — the listing's controlled callbacks, each a canonical
  // URL write. A facet or size change resets to page 1 (an offset into a
  // now-different set is meaningless); page 1 and the default size are omitted.
  const onFiltersChange = useCallback(
    (filters: ProductFilters) => {
      applyUrl((params) => {
        for (const key of FACET_KEYS) params.delete(key);
        appendFilterParams(params, filters);
        params.delete(PRODUCTS_PARAMS.page);
      });
    },
    [applyUrl],
  );
  // Story 7.29 (ADO-2025, D-7.29-37): a read that answered the selection on screen prunes it to
  // what the server applied. The URL follows with a REPLACE (the shopper asked for nothing, as with
  // a sanitising rewrite, Decision 10) that keeps the page: the server ignored the dropped values,
  // so the page it answered is the pruned selection's page.
  const onFiltersPrune = useCallback(
    (applied: ProductFilters) => {
      applyUrl((params) => {
        for (const key of FACET_KEYS) params.delete(key);
        appendFilterParams(params, applied);
      }, true);
    },
    [applyUrl],
  );
  // The listing pages at its committed size, which also corrects a size left in the
  // URL by a failed "Per page" change.
  const onPageChange = useCallback(
    (nextPage: number, pageSize: number) => {
      applyUrl((params) => writeBrowsePagination(params, nextPage, pageSize));
    },
    [applyUrl],
  );
  const onPageSizeChange = useCallback(
    (nextSize: number) => {
      applyUrl((params) => writeBrowsePagination(params, 1, nextSize));
    },
    [applyUrl],
  );
  // "Clear" on the filter panel resets facets + scope + the taxonomy drill as ONE
  // atomic URL change (one history entry), so the three can't race through
  // separate writes made off the same stale query string. The applied page size
  // is a user preference, not a filter, so it stays (Story 7-FU-C).
  const clearListing = useCallback(() => {
    drill.navigate(null);
    applyUrl((params) => {
      for (const key of FACET_KEYS) params.delete(key);
      for (const key of PRODUCT_SCOPE_PARAM_KEYS) params.delete(key);
      params.delete(SCOPE_LABEL_PARAM);
      params.delete(PRODUCTS_PARAMS.node);
      params.delete(PRODUCTS_PARAMS.page);
    });
  }, [applyUrl, drill]);
  // Story 7.31 (ADO-1113): the rail's "On Offer" checkbox and its chip enter or leave the
  // collected offers. A user action, so a history entry, and a different set, so page 1.
  const changeCollectedOffers = useCallback(
    (on: boolean) => {
      applyUrl((params) => {
        params.delete(COLLECTED_OFFERS_PARAM);
        appendCollectedOffersParam(params, on);
        params.delete(PRODUCTS_PARAMS.page);
      });
    },
    [applyUrl],
  );
  // A listing resolved: restore the drill from its breadcrumb when it disagrees
  // with the listing (a deep link / reload / Back arrives with an empty or stale
  // drill), and canonicalise a server page-clamp into the URL (Decision 9), a
  // REPLACE, since the user did not ask for the clamped page. A drill that already
  // selects the resolved node (the user picked it, or a page/facet change) is left
  // alone, so a Category column the user is revealing in the open mega-menu is not
  // collapsed under the pointer. Once the URL holds the served page/size, nothing is
  // written, so the write cannot loop.
  //
  // Both happen only while the latest URL still names the request this response answers
  // (node, facets, scope, collection, page and size). The URL can move on before that move
  // has rendered (a newer write, a node picked in the mega-menu, Back/Forward, a <Link>), and
  // the old response must then neither pull the drill back to its node nor put its clamped
  // page on a URL it does not answer. The response for the URL now current does both itself.
  const onListingResolved = useCallback(
    (resolved: {
      breadcrumb: readonly CategoryNode[];
      page: number;
      pageSize: number;
      requested: ListingRequest;
    }) => {
      const latest = latestUrl();
      if (latest.pathname !== pathname) return;
      if (!urlNamesListingRequest(new URLSearchParams(latest.search), resolved.requested)) return;
      const resolvedNodeId = resolved.breadcrumb.at(-1)?.id ?? null;
      if ((drill.selectedNode?.id ?? null) !== resolvedNodeId) drill.hydrate(resolved.breadcrumb);
      const { requested } = resolved;
      if (resolved.page === requested.page && resolved.pageSize === requested.pageSize) return;
      applyUrl((params) => writeBrowsePagination(params, resolved.page, resolved.pageSize), true);
    },
    [applyUrl, drill, latestUrl, pathname],
  );
  // The named node was missing (a stale deep link): clear the optimistic drill so
  // the mega-menu stops highlighting it. The listing renders the not-found state.
  const onListingNotFound = useCallback(() => {
    drill.navigate(null);
  }, [drill]);

  // Escape and click-outside / hover-out close the mega-menu (the latter two
  // via onBrowseDismiss below); it also closes on toggling Browse again,
  // navigating home, committing a search, or picking a leaf node (see
  // onBrowseClick / clearSearch / BrowseMenu onSelectNode).
  useEffect(() => {
    if (!browseOpen) return;
    const onKey = (e: KeyboardEvent) => {
      // Claim the Escape (preventDefault) so a window-level layer — the upsell
      // toast — does not also dismiss on the same key (PR #279 review).
      if (e.key === 'Escape') {
        e.preventDefault();
        setBrowseOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [browseOpen]);

  // `--shoplink-header-offset` (the filters rail's sticky top/height input) is
  // now measured and written by SiteHeader itself: it owns the pin state, so it
  // can swap the offset between full-header and sub-nav-only heights live
  // (Epic 7 scroll-minimize — the old whole-header ResizeObserver here couldn't
  // see the pinned state).

  return (
    <div className={styles.page}>
      <SiteHeader
        searchValue={searchInput}
        onSearchChange={(value) => {
          setSearchInput(value);
          setTypeaheadOpen(true); // every keystroke (re)opens the dropdown
        }}
        onSearchSubmit={submitSearch}
        onSearchFocus={() => setTypeaheadOpen(true)}
        searchInputRef={searchInputRef}
        searchTypeahead={
          <SearchTypeahead
            query={searchInput}
            open={typeaheadOpen}
            listboxId={typeaheadGroupId}
            onClose={() => setTypeaheadOpen(false)}
            onCommit={commitFromTypeahead}
          />
        }
        onClearSearch={clearSearch}
        // The home link itself navigates to the bare `/products`, so this writes no
        // URL of its own (a second write would push a second history entry).
        onNavigateHome={() => {
          setSearchInput('');
          setTypeaheadOpen(false);
          setBrowseOpen(false);
        }}
        logo={logo}
        themed={themed}
        favouritesCount={favouritesCount}
        // Browse toggles the department mega-menu and exits an active search; with
        // no search it leaves the listing's facets and pagination alone.
        onBrowseClick={() => {
          clearSearch();
          setBrowseOpen((o) => !o);
        }}
        browseExpanded={browseOpen}
        browseMenuId={BROWSE_MENU_ID}
        // Clicking away from the menu, or letting the pointer leave the header
        // area, puts it away as well.
        onBrowseDismiss={() => setBrowseOpen(false)}
        depots={depots}
        activeDepotId={activeDepotId}
        onSelectDepot={onSelectDepot}
        depotName={depotName}
        depotCutOff={depotCutOff}
        showManagerDashboard={isAdminRole(role)}
        planogramNav={planogramsAvailable ? PLANOGRAM_NAV : undefined}
        // Barcode scan (Story 7.18) — the catalogue is the surface that offers it;
        // the control is capability-gated (disabled-with-reason where unavailable).
        onScanClick={() => setScanOpen(true)}
        scanAvailable={scanCapability.available}
        scanUnavailableReason={scanCapability.reason}
        scanActive={scanOpen}
        {...cart}
      >
        {browseOpen && (
          <BrowseMenu
            id={BROWSE_MENU_ID}
            activeDepartmentId={drill.activeDepartment?.id ?? null}
            activeCategoryId={drill.activeCategory?.id ?? null}
            selectedNodeId={drill.selectedNode?.id ?? null}
            onClose={() => setBrowseOpen(false)}
            onSelectNode={(node) => {
              if (favouritesView) {
                goToCatalogue(node);
                setBrowseOpen(false);
                return;
              }
              // Story 7.35 (ADO-2032): a mega-menu selection commits `?node=` to
              // the URL (which also drops any active search + facets + pagination)
              // and drives the drill optimistically. Close once a leaf (Sub-Category,
              // or a childless node) is chosen.
              selectBrowseNode(node);
              setTypeaheadOpen(false);
              if (!node.hasChildren) setBrowseOpen(false);
            }}
            onExpandNode={drill.expandNode}
          />
        )}
      </SiteHeader>

      <main className={styles.main} aria-label="Products">
        {searching ? (
          // Full-bleed like the browse listing: the embedded search body now
          // carries the SAME re-skinned listing shell (pinned filters rail flush
          // to the viewport's left edge + content column with its own responsive
          // padding) — no extra wrapper padding here (Epic 7 pixel parity).
          // Keyed by the ordering entry (Enabler E7-C, ADO-2274, D-1): another entry is another
          // range, so the search starts afresh, re-runs the committed term and drops its filters.
          <ProductSearch
            key={catalogueEntryKey ?? 'no-entry'}
            embedded
            term={committedTerm}
            runToken={searchNonce}
            filters={requestFilters}
            onFiltersChange={onFiltersChange}
            onFiltersPrune={onFiltersPrune}
            // Story 7.31 (ADO-1113), AC1 (PR #862 review): a term committed inside the collected
            // offers keeps `offers=all` in the URL, so the search searches the collection.
            collectedOffers={collectedOffers}
            onCollectedOffersChange={changeCollectedOffers}
          />
        ) : (
          <CategoryListing
            node={
              favouritesView
                ? drill.selectedNode
                : drill.selectedNode?.id === urlState.node
                  ? drill.selectedNode
                  : null
            }
            onNavigate={listingNavigate}
            favouritesView={favouritesView}
            onViewAll={favouritesView ? () => goToCatalogue(null) : undefined}
            scope={scope}
            scopeLabel={scopeLabel}
            onScopeChange={changeScope}
            collectedOffers={collectedOffers}
            onCollectedOffersChange={changeCollectedOffers}
            offerCateringOnly={offerCateringOnly}
            urlControlled={!favouritesView}
            // The favourites view narrows through its in-memory drill (`node`), not the URL.
            nodeId={favouritesView ? undefined : urlState.node}
            filters={requestFilters}
            onFiltersChange={onFiltersChange}
            onFiltersPrune={onFiltersPrune}
            page={requestPage}
            pageSize={urlState.pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onClearAll={clearListing}
            onResolved={onListingResolved}
            onNotFound={onListingNotFound}
            toolbarAction={
              planogramsAvailable && phoneLayout && !favouritesView ? <PlanogramChip /> : undefined
            }
          />
        )}
      </main>

      {/* Camera barcode scan (Story 7.18) — self-contained flow; on a confirmed
          match it adds through the Story 7.9 one-click path (same cart/toast). It
          never writes to the search box, so the search field stays clean (AC8). */}
      <ScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        // "Search by name" fallback (AC7/AC12): the modal closes first (restoring focus
        // to the trigger), so defer the focus a frame to land it on the search input.
        onUseTextSearch={() => requestAnimationFrame(() => searchInputRef.current?.focus())}
      />
    </div>
  );
}

export default Products;

/** The facet selection as a stable string (its canonical query form), for memoising. */
function filtersQueryOf(filters: ProductFilters): string {
  const params = new URLSearchParams();
  appendFilterParams(params, filters);
  return params.toString();
}

/** Only the collection facets of a selection ({@link COLLECTION_FILTER_KEYS}). */
function collectionFiltersOf(filters: ProductFilters): ProductFilters {
  const kept: { -readonly [K in FacetKey]?: readonly string[] } = {};
  for (const key of COLLECTION_FILTER_KEYS) {
    const values = filters[key];
    if (values !== undefined) kept[key] = values;
  }
  return kept;
}

/** Whether the URL (no search active) names exactly the listing `requested` asked for. */
function urlNamesListingRequest(params: URLSearchParams, requested: ListingRequest): boolean {
  const state = parseProductsUrlState(params);
  return (
    !state.searching &&
    state.node === requested.nodeId &&
    state.page === requested.page &&
    state.pageSize === requested.pageSize &&
    filtersQueryOf(state.filters) === filtersQueryOf(requested.filters) &&
    scopeQueryOf(productScopeFromSearch(params)) === scopeQueryOf(requested.scope) &&
    collectedOffersFromSearch(params) === requested.collectedOffers
  );
}

/** The scope as a stable string (its canonical query form), for memoising. */
function scopeQueryOf(scope: ProductScope): string {
  const params = new URLSearchParams();
  appendScopeParams(params, scope);
  return params.toString();
}
